import {Col, Form, Input, Radio, RadioChangeEvent, Row, Select, Spin} from 'antd';
import {FormInstance} from 'antd/lib/form';
import React, {useEffect, useRef, useState} from 'react';
import {apiFetchMappingWithQualifier} from "../../../../containers/QueryBuilder/queryBuilderApi";
import _ from "lodash";
import {MAX_SIZE, MIN_INPUT_SIZE, ValueType} from "./RemoteSelect";
import {SelectProps} from "antd/es/select";
import {LabelAndValue} from "../../../create/types/LabelAndValue";

import RemoteSelectAllButton from "./RemoteSelectAllButton";

const QUALIFIER_ALL = 'All'
const QUALIFIER_LIST = [
    QUALIFIER_ALL,
    'Disorder',
    'Procedure',
    'Observable Entity',
    'Finding',
    'Record Artifact',
]

const SEARCH_TYPE_WORD = 'WORD'
const SEARCH_TYPE_CHARACTER = 'CHARACTER'

interface RemoteSelectProps<ValueType = any> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    form: FormInstance
    fieldId: string
}

// TODO: this is very similar to RemoteSelect
const ClinicalEventFilter = ({form, fieldId, ...props}: RemoteSelectProps) => {
    const [search, setSearch] = useState<string | undefined>(undefined)
    const [qualifier, setQualifier] = useState<string>(QUALIFIER_ALL.toLowerCase())
    const [searchType, setSearchType] = useState<string>(SEARCH_TYPE_CHARACTER)
    const [fetching, setFetching] = useState(false)
    const [options, setOptions] = useState<ValueType[]>([])
    const fetchRef = useRef(0)

    const onSelectAll = () => {
        const totalChoices = [...(form.getFieldValue(fieldId) || []), ...options];
        const uniqueChoices = totalChoices.reduce((prev: { label: string, value: number }[], current:{ label: string, value: number }) => {
            const isPresent = prev.find(itemInArray => itemInArray.value === current.value) !== undefined;
            if (isPresent) {
                return prev;
            }
            return [...prev, current];
        }, []);
        form.setFieldValue(fieldId, uniqueChoices)
    }

    useEffect(() => {
        debounceFetcher(search!, qualifier, searchType)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, qualifier, searchType])

    const debounceFetcher = _.debounce((value: string, qualifier: string, searchType: string) => {
        const fetchOptions = (searchString: string): Promise<LabelAndValue[]> => {
            let formattedQualifier = undefined
            if (qualifier !== QUALIFIER_ALL.toLowerCase()) {
                formattedQualifier = ` (${qualifier})`
            }
            return apiFetchMappingWithQualifier(fieldId, searchString, formattedQualifier, searchType, MAX_SIZE)
                .then(i => {
                    return i.map(j => ({label: `${j.opt} (${j.code})`, value: j.code}))
                })
        }

        if (value && value.length >= MIN_INPUT_SIZE) {
            fetchRef.current += 1
            const fetchId = fetchRef.current
            setOptions([])
            setFetching(true)
            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return
                }
                setOptions(newOptions)
                setFetching(false)
            })
        }
    }, 500)

    const onQualifierChange = (e: RadioChangeEvent) => {
        const qualifier = e.target.value
        setQualifier(qualifier)
    }

    const onSearchTypeChange = (e: RadioChangeEvent) => {
        const searchType = e.target.value
        setSearchType(searchType)
    }

    const searchStatus = () => {
        if (fetching) {
            return <Spin/>
        } else if (search) {
            let text = [<>Your search for <b>{search}</b></>]
            if (qualifier !== QUALIFIER_ALL.toLowerCase()) {
                text.push(<> with <b>{qualifier}</b></>)
            }
            text.push(<>, found {options.length} item(s).</>)
            return <>
                {text.map((e, i) => <e.type key={i} {...e.props} />)}
                {<RemoteSelectAllButton onSelectAll={onSelectAll} />}
            </>
        }
        return <> </>
    }

    return <Row>
        <Col span={20} style={{padding: "5px"}}>
            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                <Input/>
            </Form.Item>
            <Form.Item extra={`Please input at least 3 chars to trigger search, for first ${MAX_SIZE} results.`}>
                <span data-testid={'search-status-text'}>{searchStatus()}</span>
                <Form.Item name={fieldId} noStyle={true}>
                    <Select
                        maxTagCount={30}
                        mode="multiple"
                        allowClear={true}
                        labelInValue
                        filterOption={false}
                        onSearch={value => setSearch(value)}
                        notFoundContent={fetching ? <Spin size="small"/> : null}
                        {...props}
                        options={options}
                    />
                </Form.Item>
            </Form.Item>
        </Col>
        <Col span={24}>
            <Form.Item name={"qualifier"} initialValue={qualifier} label={"Category"}>
                <Radio.Group onChange={onQualifierChange}>
                    {QUALIFIER_LIST.map(i => <Radio key={i} value={i.toLowerCase()} data-testid={`clinical-event-qualifier-${i.toLowerCase()}`}>{i}</Radio>)}
                </Radio.Group>
            </Form.Item>
        </Col>
        <Col span={24}>
            <Form.Item name={"searchType"} initialValue={searchType} label={"Search Type"}>
                <Radio.Group onChange={onSearchTypeChange}>
                    <Radio key={1} value={SEARCH_TYPE_CHARACTER}>Character Match</Radio>
                    <Radio key={2} value={SEARCH_TYPE_WORD}>Word Match</Radio>
                </Radio.Group>
            </Form.Item>
        </Col>
    </Row>
}

export default ClinicalEventFilter